import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ActionService } from 'src/app/core/services/action.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { CommonService } from 'src/app/core/services/common.service';
import { RouteService } from 'src/app/core/services/route.service';
import { LoadApplicationTemplates } from 'src/app/core/state/_actions/application.actions';
import {
  DeleteField
} from 'src/app/core/state/_actions/field.actions';
import { ApplicationTemplatePayload } from 'src/app/core/state/_models/application.models';
import { RequestCompanyDatatable } from 'src/app/core/state/_models/company.model';
import { Field } from 'src/app/core/state/_models/field.model';
import { OpTypes } from 'src/app/core/state/_models/general.model';
import { Datatable } from 'src/app/core/state/_models/report.models';
import { Template } from 'src/app/core/state/_models/template.model';
import { getApplicationOperations, getTemplates } from 'src/app/core/state/_selectors/application.selector';
import { FormService } from 'src/app/core/state/_services/form.service';
import { UpsertComponent } from 'src/app/shared/components/templates/upsert/upsert.component';
import { Roles } from 'src/app/shared/enums/role.enum';
import {
  alertPresets,
  AlertService,
} from 'src/app/shared/services/alert.service';
import { SearchService } from 'src/app/shared/services/search.service';
import { SubSink } from 'subsink';
import { SweetAlertOptions } from 'sweetalert2';

@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  host:{ class: 'content-fit-table-parent'}
})
export class ViewComponent implements OnInit, OnDestroy {
  @Input('recordId') recordId: number = 0;
  @Input('appId') appId: string = '';
  @Input('fieldId') fieldId: string = '';
  @Input('tableId') tableId: string = '';
  @Input('modalRef') modalRef: any;
  subsink = new SubSink();
  columns:any = [
    { Name: 'Name', FieldName:'name'},
    { Name: 'Description', FieldName:'description'},
  ];
  defaultFields = [
    'createdby',
    'modifiedby',
    'datecreated',
    'datemodified',
    'id',
  ];
  dTcolumns = [
    'id',
    'name',
    'file_name',
    'date_created',
    'date_modified',
    'created_by',
    'modified_by'
  ];
  templates: Array<Template> = [];
  trigger: Subject<boolean> = new Subject<boolean>();
  dataset: Datatable = <Datatable>{};
  datasetArr: any;
  templateForm!: UntypedFormGroup;
  selectedTemplate: Array<any> = [];

  constructor(
    private route: RouteService,
    private store: Store,
    private search: SearchService,
    private action: ActionService,
    private alert: AlertService,
    private modal: NgbModal,
    private authService: AuthService,
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private common:CommonService
  ) {}

  ngOnInit(): void {
    this.templateForm = this.fb.group({
      Template: [
          null,
          [
            Validators.required,
          ]
      ]
    });
    this.loadSubscribers();
    this.subsink.sink = this.route.fork(this.init.bind(null));
    this.init();
  }

  init = () => {
    this.store.dispatch(new LoadApplicationTemplates(this.route.getApplicationKey(),true));
  };

  /**
   * Subscribers
   */
  loadSubscribers = () => {
    this.subsink.add(
      this.store
        .pipe(
          select(getTemplates),
          filter((templates) => !!templates[this.route.getApplicationKey()]),
          map((templates) => templates[this.route.getApplicationKey()])
        )
        .subscribe((templates) => {
          this.templates = JSON.parse(JSON.stringify(templates));
          this.createDataset(this.templates);
        }));
  };

  createDataset(templates: any) {
    this.dataset = {
      Draw: '1',
      GetAll: true,
      RecordsFiltered: templates.length,
      RecordsTotal: templates.length,
      Data: this.getPaginatedFields(this.getTemplateData(templates), 0, 50)
    };
  }

  getTemplateData(templates: Template[]) {
    this.datasetArr = templates.map((template: ApplicationTemplatePayload) => {
      return {
        id:  template.Key,
        name: template.Name,
        description: template.Description,
        file_name: template.FileName,
      };
    });
    return this.datasetArr;
  }

  /**
   * @returns true if field name passes search filter
   */
  searchFilter = (field: Field) => this.search.filter(field.Name);

  /**
   * Delete template
   */
  deleteTemplate = (row: any) => {
    const template = this.getTemplate(row);
    this.alert
      .fire(<SweetAlertOptions>alertPresets.delete)
      .then(async (res) => {
        if (res.isConfirmed) {
          try {
            await this.action.dispatch(
              OpTypes.DELETE,
              new DeleteField(this.route.getApplicationKey(), template.Key),
              getApplicationOperations
            );
          } catch (err) {}
        }
      });
  };

  /**
   * Edit template
   */
  editTemplate = (row: any) => {
    const template = this.getTemplate(row);
    const modal = this.modal.open(UpsertComponent, {
      windowClass: 'modal-custom',
      centered: true,
      backdrop: 'static',
      animation: true,
      keyboard: false,
    });
    modal.componentInstance.key = template?.Key;
    modal.componentInstance.name = template?.Name;
    modal.componentInstance.description = template?.Description;
    modal.componentInstance.fileName = template?.FileName;
  };

  convertTimeToEST(timestamp: any) {
    return timestamp;
    // var a = moment
    //   .utc(timestamp)
    //   .format('MM/DD/YYYY h:mm A zz');
    // return a;
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * @returns true if super_admin
   */
  get isSuperAdmin() {
    return this.authService.userProfile?.Role === Roles.superAdmin;
  }

  /**
   * @returns true if admin
   */
  get isAdmin() {
    return this.authService.userProfile?.Role === Roles.admin;
  }

  getTemplate(row: any) {
    const template = this.templates.filter((e) =>  (e.Key === row.key));
    return template[0];
  }

  /**
   * Payload listener
   */
  payloadListener = (event: RequestCompanyDatatable) => {
    if (event.Template.length > 0) {
      this.selectedTemplate = event.Template;
      this.templateForm.controls.Template.setValue(this.selectedTemplate[0]);
    }

    let order = event.Order[0];

    // sort columns
    let column: any = this.dTcolumns[order?.column];
    if (order?.dir === 'asc' && order?.column >= 0) {
      this.datasetArr?.sort((a: any, b: any) =>
        a[column] > b[column] ? 1 : a[column] < b[column] ? -1 : 0
      );
    } else if (order?.dir === 'desc' && order?.column >= 0) {
      this.datasetArr?.sort((a: any, b: any) =>
        a[column] < b[column] ? 1 : a[column] > b[column] ? -1 : 0
      );
    }

    let searchArr: any = []; // contains all rows which meets search criteria
    let searchCols = event.Columns?.filter((e) => e.search.value !== '');
    let globalSearch = event.Search?.value?.toLowerCase()?.trim();

    searchArr = this.common.getFilteredData(searchCols,globalSearch,searchArr,this.datasetArr,this.dTcolumns);

      let sortArr: any;
      if (searchCols?.length || searchArr.length)
        sortArr = this.getPaginatedFields(searchArr, event.Start, event.Length);
      else if (globalSearch) {
        sortArr = [];
      } else
        sortArr = this.getPaginatedFields(this.datasetArr, event.Start, event.Length);

    this.dataset = {
      Draw: '1',
      GetAll: true,
      RecordsFiltered: (searchCols?.length || searchArr.length || globalSearch || event?.ConditionGroups?.length) ? searchArr?.length : this.datasetArr?.length,
      RecordsTotal: (searchCols?.length || searchArr.length || globalSearch || event?.ConditionGroups?.length) ? searchArr?.length : this.datasetArr?.length,
      Data: sortArr,
    };
  };

  getPaginatedFields(arr: any, start: any, size: any) {
    if (start + size <= arr?.length) return arr?.slice(start, start + size);
    else if (start > arr?.length) return arr;
    return arr?.slice(start);
  }

  viewRow = (row: any) => {
    console.log(row);
  };

  /**
   * Close modal
   */
  close = () => {
    this.templates = [];
    this.dataset = <Datatable>{};
    if(this.modalRef){
      this.modalRef.close();
    } else {
      this.modal.dismissAll();
    }
  };

  /**
   * Submit
   */
  onSubmit = async () => {
    let payload = {
      FieldId: this.fieldId,
      AppId: this.appId,
      TableId: this.tableId,
      RecordId: this.recordId,
      TemplateId: this.selectedTemplate[0].id,
      TemplateFileName: this.selectedTemplate[0].file_name
    }
    if(this.recordId){
      this.subsink.add(
      this.formService.selectTemplate(payload).subscribe(
        (res: any) => {
          console.log(res);
        },
        (error: any) => {
          console.log(error);
        },
        () => {
          this.templates = [];
          this.dataset = <Datatable>{};
          this.close();
        }));
      } else {
        localStorage.setItem(`${this.tableId}_${this.fieldId}_name`,this.selectedTemplate[0].file_name)
        localStorage.setItem(`${this.tableId}_${this.fieldId}_file`,JSON.stringify(payload))
        this.templates = [];
        this.dataset = <Datatable>{};
        this.close();
    }


  };

}
